import React from "react";
import { useDispatch, useSelector } from "react-redux";

import FileUploadPane, {
  FileUploadPaneConfig,
} from "src/app/components/FileUploadPane/FileUploadPane";
import useGetProjectContentGuidelinesData from "src/app/project/hooks/useGetProjectContentGuidelinesData";
import {
  getProject,
  setOpenContentGuidelinesPane,
  setLoadingContentGuidelines,
} from "src/redux";
import { RootState } from "src/redux/reducers";
import { uploadContentGuidelinesFile } from "./utils";
import { API_URLS } from "src/utils/API_URLS";
import { wsAxiosDelete, wsAxiosPut } from "src/helpers/wsAxios";
import { MAX_SIZE_SINGLE_ATTACHMENT } from "src/utils/uploadFunctionsUtils";

const CONTENT_GUIDELINES_CONFIG: FileUploadPaneConfig = {
  title: {
    id: "ws_add_content_guidelines_title",
    defaultMessage: "Add content guidelines",
  },
  description: {
    id: "ws_add_content_guidelines_description",
    defaultMessage: `Upload a file containing the content guidelines.AI will analyze this file and extract the key parameters describing the content guidelines and present them in the "Content requirements" field below. You can adjust these requirements manually at any time.`,
  },
  textareaLabel: {
    id: "ws_content_guidelines_textarea_label",
    defaultMessage: "Content requirements",
  },
  textareaTooltip: {
    id: "ws_content_guidelines_textarea_tooltip",
    defaultMessage:
      "These requirements guide AI-powered features, enabling content analysis and validation across Content and Publications.",
  },
  textareaPlaceholder: {
    id: "ws_content_guidelines_textarea_placeholder",
    defaultMessage:
      "List must-haves, do's & don'ts, formats, and key requirements for content",
  },
  acceptedFileTypes: {
    "application/pdf": [".pdf"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      ".docx",
    ],
    "text/plain": [".txt"],
  },
  maxFileSizeMB: MAX_SIZE_SINGLE_ATTACHMENT,
};

export default function ContentGuidelinesPane() {
  const dispatch = useDispatch();

  const isLoadingContentGuidelines = useSelector(
    (state: RootState) => state.projectReducer.isLoadingContentGuidelines,
  );

  const { projectBasicData } = useSelector(
    (state: RootState) => state.projectReducer,
  );

  const activeWorkspaceUuid = useSelector(
    (state: RootState) => state.mainReducer.activeWorkspaceUuid,
  );

  const {
    contentGuidelinesFileName,
    contentGuidelinesContent,
    contentGuidelinesMetaFieldUuid,
    contentGuidelinesFileData,
  } = useGetProjectContentGuidelinesData();

  const projectUuid = projectBasicData?.projectId || "";
  const contentGuidelinesAssetUuid = contentGuidelinesFileData?.assetId || "";
  const contentGuidelinesFileUrl =
    contentGuidelinesFileData?.publicDownloadUrl ||
    contentGuidelinesFileData?.publicPreviewUrl;

  const handleCloseContentGuidelinesPane = () => {
    dispatch(setOpenContentGuidelinesPane(false));
  };

  const updateProject = () => {
    dispatch(getProject(projectUuid));
  };

  const handleUploadFile = async (file: File): Promise<string | undefined> => {
    dispatch(setLoadingContentGuidelines(true));

    try {
      const response = await uploadContentGuidelinesFile({
        file,
        wsMetaFieldUuid: contentGuidelinesMetaFieldUuid,
        wsProjectUuid: projectUuid,
        wsWorkspaceUuid: activeWorkspaceUuid,
      });
      if (response) {
        return response.result;
      }
    } finally {
      dispatch(setLoadingContentGuidelines(false));
    }
  };

  const handleRemoveFile = (onSuccess: () => void) => {
    dispatch(setLoadingContentGuidelines(true));
    const url = API_URLS.deleteProjectMetaFieldFileAsset
      .replace(":fieldId:", contentGuidelinesMetaFieldUuid)
      .replace(":assetId:", contentGuidelinesAssetUuid);
    wsAxiosDelete({
      url,
      thenCallback: () => {
        onSuccess();
        updateProject();
      },
      finallyCallback: () => {
        dispatch(setLoadingContentGuidelines(false));
      },
    });
  };

  const handleSaveGuidelines = (guidelines: string) => {
    wsAxiosPut(
      API_URLS.setProjectAiFileAnalysis,
      {
        wsProjectUuid: projectUuid,
        metaFieldUuid: contentGuidelinesMetaFieldUuid,
        content: guidelines,
      },
      "ws_error_while_applying_guidelines",
    );
  };

  return (
    <FileUploadPane
      config={CONTENT_GUIDELINES_CONFIG}
      onClose={handleCloseContentGuidelinesPane}
      handleUploadFile={handleUploadFile}
      handleRemoveFile={handleRemoveFile}
      handleSaveContent={handleSaveGuidelines}
      content={contentGuidelinesContent}
      fileName={contentGuidelinesFileName}
      fileUrl={contentGuidelinesFileUrl}
      isLoading={isLoadingContentGuidelines}
    />
  );
}
